import { useEffect, useState, useCallback } from 'react'
import { apiAdminGetAuditLogs, ApiRequestError, type AdminAuditLog } from '@/lib/api'
import type { PaginatedResult } from '@nexbit/types'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertCircle, ChevronLeft, ChevronRight, Eye, Search, X } from 'lucide-react'
import { useT } from '@/contexts/language'
import { ictEndExclusive, ictStart } from '@/lib/number-limit-history'

/**
 * Thai descriptions for every audit action code. The `${status}` codes expand to
 * .active / .suspended; both are listed. Unknown codes fall back to the raw string
 * so a newly-added action is still legible (just untranslated) until added here.
 */
const ACTION_LABELS: Record<string, string> = {
  'account.change_password': 'เปลี่ยนรหัสผ่าน',
  'admin.create': 'สร้างแอดมิน',
  'admin.active': 'เปิดใช้งานแอดมิน',
  'admin.suspended': 'ระงับแอดมิน',
  'admin.permissions': 'แก้สิทธิ์แอดมิน',
  'agent.create': 'สร้างเอเจนต์',
  'ip_whitelist.create': 'เพิ่ม IP ที่อนุญาต (Partner API)',
  'ip_whitelist.delete': 'ลบ IP ที่อนุญาต (Partner API)',
  'partner.set_wallet_api_key': 'ตั้ง Wallet API Key',
  'agent.active': 'เปิดใช้งานเอเจนต์',
  'agent.suspended': 'ระงับเอเจนต์',
  'agent.update': 'แก้ไขเอเจนต์',
  'bet.rollback': 'คืนโพย (rollback)',
  'draw.create': 'เปิดงวดหวย',
  'draw.close': 'ปิดรับแทงงวด',
  'draw.result.enter': 'กรอกผลหวย',
  'draw.result.edit': 'แก้ผลหวย',
  'draw.settle': 'จ่ายเงินรางวัล',
  'draw.void': 'ยกเลิกการจ่าย (void)',
  'draw.limits.update': 'แก้วงเงินรับแทง',
  'draw.payout.update': 'แก้อัตราจ่าย',
  'draw.lottery.settings': 'ตั้งค่าหวย',
  'draw.lottery.enable': 'เปิดการมองเห็นหวย',
  'draw.lottery.disable': 'ซ่อนหวย',
  'draw.market.open': 'เปิดตลาด',
  'draw.market.close': 'ปิดตลาด',
  'draw.number.block': 'อั้นเลข (ปิดรับ)',
  'draw.number.unblock': 'ยกเลิกอั้นเลข',
  'draw.number_cap.set': 'ตั้งเพดานรายเลข',
  'draw.number_cap.delete': 'ลบเพดานรายเลข',
  'partner.number_rule.set': 'เอเย่นต์ตั้งอั้นเลข',
  'partner.number_rule.delete': 'เอเย่นต์ลบอั้นเลข',
  'partner.number_rule.clear': 'เอเย่นต์ล้างอั้นเลขทั้งหมด',
  'game.update': 'แก้ไขเกม',
  'master.create': 'สร้างมาสเตอร์',
  'master.active': 'เปิดใช้งานมาสเตอร์',
  'master.suspended': 'ระงับมาสเตอร์',
  'master.cap': 'ตั้งเพดานหุ้นมาสเตอร์',
  'member.create': 'สร้างสมาชิก',
  'member.active': 'เปิดใช้งานสมาชิก',
  'member.suspended': 'ระงับสมาชิก',
  'member.impersonate': 'เข้าใช้งานแทนสมาชิก',
  'partner.create': 'สร้างพาร์ทเนอร์',
  'partner.update': 'แก้ไขพาร์ทเนอร์',
  'partner.rotate_key': 'รีเซ็ต API key',
  'partner.rotate_nexbit_api_key': 'รีเซ็ต API key',
  'partner.rotate_launch_secret': 'รีเซ็ต launch secret',
  'provider.update': 'แก้ไขผู้ให้ข้อมูล',
  'settings.telegram.update': 'แก้ตั้งค่า Telegram',
}

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action
}

function actionVariant(action: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (action.includes('create') || action.includes('enter') || action.includes('settle')) return 'success'
  if (action.includes('void') || action.includes('rollback') || action.includes('suspended') || action.includes('disable') || action.includes('close') || action.includes('block')) return 'destructive'
  if (action.includes('update') || action.includes('rotate') || action.includes('edit') || action.includes('permissions') || action.includes('cap') || action.includes('settings')) return 'warning'
  return 'outline'
}

// ictStart / ictEndExclusive come from lib/number-limit-history — the same [from, to)
// convention, tested there. Two copies of a date-window helper is how one screen ends up
// including a day the other excludes.

function DetailDialog({ log, onClose }: { log: AdminAuditLog | null; onClose: () => void }) {
  return (
    <Dialog open={!!log} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">รายละเอียดกิจกรรม</DialogTitle>
        </DialogHeader>
        {log && (
          <div className="space-y-2 text-xs">
            <Field label="กิจกรรม" value={actionLabel(log.action)} />
            <Field label="รหัส" value={log.action} mono />
            <Field label="ผู้ทำ" value={log.actorUsername ?? log.actorId ?? '(ระบบ)'} />
            <Field label="รายการ" value={`${log.entityType ?? '—'} / ${log.entityId?.slice(-8) ?? '—'}`} mono />
            <Field label="IP" value={log.ipAddress ?? '—'} mono />
            <Field label="เวลา" value={format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm:ss')} />
            {/* The before-side. Written since the start and never shown, so a changed
                ceiling appeared with no record of the figure it replaced — which is the
                side a dispute over a payout turns on. */}
            {log.oldValue != null && <Payload label="ค่าก่อนแก้" value={log.oldValue} />}
            {log.newValue != null && (
              <Payload label={log.oldValue != null ? 'ค่าหลังแก้' : 'ข้อมูล'} value={log.newValue} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Payload({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
      <pre className="bg-secondary/30 rounded-[var(--radius-sm)] p-2 text-[10px] overflow-auto max-h-48 whitespace-pre-wrap font-mono">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground w-20 shrink-0 mt-0.5">{label}</span>
      <span className={mono ? 'font-mono text-foreground break-all' : 'text-foreground break-all'}>{value}</span>
    </div>
  )
}

export default function AdminAuditPage() {
  const t = useT()
  const [data, setData] = useState<PaginatedResult<AdminAuditLog> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [detail, setDetail] = useState<AdminAuditLog | null>(null)

  // Date filter — `from`/`to` are the inputs; `applied` is what the last search sent,
  // so editing the inputs doesn't refetch until the user presses ค้นหา.
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [applied, setApplied] = useState<{ from?: string; to?: string }>({})

  const load = useCallback(async (p: number, f: { from?: string; to?: string }) => {
    setLoading(true)
    setError(null)
    try {
      setData(await apiAdminGetAuditLogs(p, 50, f))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { void load(page, applied) }, [load, page, applied])

  function search() {
    setPage(1)
    setApplied({
      from: from ? ictStart(from) : undefined,
      to: to ? ictEndExclusive(to) : undefined,
    })
  }
  function clear() {
    setFrom(''); setTo(''); setPage(1); setApplied({})
  }

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0
  const hasFilter = !!(applied.from || applied.to)

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <div className="mb-4">
        <h1 className="text-lg font-semibold tracking-tight">{t.audit.title}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">ประวัติการทำงานของแอดมินและระบบ</p>
      </div>

      {/* Date-range filter */}
      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 border border-border rounded-[var(--radius)] bg-card">
        <div className="space-y-1">
          <Label htmlFor="af-from" className="text-xs text-muted-foreground">ตั้งแต่</Label>
          <Input id="af-from" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40 font-mono text-sm" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="af-to" className="text-xs text-muted-foreground">ถึง</Label>
          <Input id="af-to" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-9 w-40 font-mono text-sm" />
        </div>
        <Button onClick={search} className="h-9 gap-1.5"><Search className="h-4 w-4" />ค้นหา</Button>
        {hasFilter && (
          <Button variant="outline" onClick={clear} className="h-9 gap-1.5"><X className="h-4 w-4" />ล้าง</Button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">รายการกิจกรรม</span>
          {data && !loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">{data.total} รายการ</span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-24" />
                <Skeleton className="ml-auto h-4 w-28" />
              </div>
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">{hasFilter ? 'ไม่พบกิจกรรมในช่วงวันที่เลือก' : t.audit.noLogs}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.common.createdAt}</TableHead>
                <TableHead>{t.audit.actor}</TableHead>
                <TableHead>{t.audit.action}</TableHead>
                <TableHead>{t.audit.entity}</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <time className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(log.createdAt), 'dd/MM HH:mm:ss')}
                    </time>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">{log.actorUsername ?? log.actorId?.slice(-8) ?? 'system'}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <Badge variant={actionVariant(log.action)} className="text-[11px] w-fit">
                        {actionLabel(log.action)}
                      </Badge>
                      <span className="font-mono text-[10px] text-muted-foreground/50">{log.action}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {log.entityType && (
                      <div>
                        <span className="font-mono text-[10px] text-muted-foreground">{log.entityType}</span>
                        {log.entityId && (
                          <span className="font-mono text-[10px] text-muted-foreground/50 ml-1">#{log.entityId.slice(-6)}</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDetail(log)} aria-label="ดูรายละเอียด">
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <span className="font-mono text-xs text-muted-foreground">{page} / {totalPages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="หน้าก่อน">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="หน้าถัดไป">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <DetailDialog log={detail} onClose={() => setDetail(null)} />
    </div>
  )
}
